import React, { useState } from "react";
import { Box, Typography, Link, IconButton } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { gworkspaceOAuthScopesCsv, GWORKSPACE_DOMAIN_WIDE_DELEGATION_URL } from "../../utils/constants";

const STEP_SX = {
  fontFamily: "var(--theo-font-sans)",
  fontWeight: 600,
  fontSize: 14,
  color: "#1a1a1a",
  mb: 1,
} as const;

const LI_SX = {
  fontFamily: "var(--theo-font-sans)",
  fontSize: 13,
  color: "#555",
  lineHeight: 1.5,
} as const;

export const GWORKSPACE_APIS: { label: string; url: string }[] = [
  {
    label: "Gmail API",
    url: "https://console.cloud.google.com/apis/library/gmail.googleapis.com",
  },
  {
    label: "Google Calendar API",
    url: "https://console.cloud.google.com/apis/library/calendar-json.googleapis.com",
  },
  {
    label: "Enterprise License Manager API",
    url: "https://console.cloud.google.com/apis/library/licensing.googleapis.com",
  },
  {
    label: "Google Vault API",
    url: "https://console.cloud.google.com/apis/library/vault.googleapis.com",
  },
  {
    label: "Google Drive API",
    url: "https://console.cloud.google.com/apis/library/drive.googleapis.com",
  },
  {
    label: "Google Chat API",
    url: "https://console.cloud.google.com/marketplace/product/google/chat.googleapis.com",
  },
  {
    label: "Google Alert Center API",
    url: "https://console.cloud.google.com/marketplace/product/google/alertcenter.googleapis.com",
  },
  {
    label: "Cloud Identity API",
    url: "https://console.cloud.google.com/apis/library/cloudidentity.googleapis.com",
  },
];

/**
 * Google Workspace — service account, domain-wide delegation, and API enablement.
 * Shared: Add Integration modal, App Configuration, and Onboarding Wizard.
 */
export const GWorkspaceSetupGuideSteps: React.FC<{
  showSetupGuideTitle?: boolean;
}> = ({ showSetupGuideTitle = true }) => {
  const [scopesCopied, setScopesCopied] = useState(false);

  const handleCopyScopes = async () => {
    try {
      await navigator.clipboard.writeText(gworkspaceOAuthScopesCsv);
      setScopesCopied(true);
      window.setTimeout(() => setScopesCopied(false), 2000);
    } catch {
      setScopesCopied(false);
    }
  };

  return (
  <>
    {showSetupGuideTitle ? (
      <Typography
        sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 15, color: "#1a1a1a", mb: 1.5 }}
      >
        Setup Guide
      </Typography>
    ) : null}

    <Typography sx={STEP_SX}>Step 1: Create Project and Service Account</Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2, "& li": { mb: 0.75 } }}>
      <Typography component="li" sx={LI_SX}>
        Log into the{" "}
        <Link
          href="https://console.cloud.google.com/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "#705d4a", fontWeight: 500 }}
        >
          Google Cloud Console
        </Link>{" "}
        and create a project (e.g., TheoAI).
      </Typography>
      <Typography component="li" sx={LI_SX}>
        Enable the <strong>Admin SDK API</strong> in the project:
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, "& li": { mb: 0.5 } }}>
          <Typography component="li" sx={LI_SX}>
            With the new project selected, navigate to <strong>APIs &amp; Services &gt; Library</strong>.
          </Typography>
          <Typography component="li" sx={LI_SX}>
            Search for <strong>Admin SDK API</strong>, click the result, and click the blue{" "}
            <strong>ENABLE</strong> button.
          </Typography>
        </Box>
      </Typography>
      <Typography component="li" sx={LI_SX}>
        Create a Service Account (e.g., onboarding-theoai) under{" "}
        <strong>IAM &amp; Admin &gt; Service Accounts</strong>.
      </Typography>
      <Typography component="li" sx={LI_SX}>
        Generate the JSON Key:
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, "& li": { mb: 0.5 } }}>
          <Typography component="li" sx={LI_SX}>
            Click on the name of the Service Account you just created.
          </Typography>
          <Typography component="li" sx={LI_SX}>
            Click the <strong>KEYS</strong> tab.
          </Typography>
          <Typography component="li" sx={LI_SX}>
            Click the <strong>ADD KEY</strong> drop-down menu and select <strong>Create new key</strong>.
          </Typography>
          <Typography component="li" sx={LI_SX}>
            Select <strong>JSON</strong> as the key type, and click <strong>CREATE</strong>.
          </Typography>
          <Typography component="li" sx={LI_SX}>
            The browser will automatically download a file containing the private key (e.g.,{" "}
            <em>[project-id]-[unique-id].json</em>).
          </Typography>
        </Box>
      </Typography>
      <Typography component="li" sx={LI_SX}>
        <strong>If key creation is not allowed:</strong>
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, "& li": { mb: 0.5 } }}>
          <Typography component="li" sx={LI_SX}>
            Disable the organization policy{" "}
            <Link
              href="https://console.cloud.google.com/iam-admin/orgpolicies/iam-disableServiceAccountKeyCreation;fromListPage=true?project=sandbox-theoai-483608"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "#705d4a", fontWeight: 600 }}
            >
              Disable service account key creation
            </Link>
            .
          </Typography>
          <Typography component="li" sx={LI_SX}>
            Give the user the <strong>Organization Policy Administrator</strong> role.
          </Typography>
          <Typography component="li" sx={LI_SX}>
            Go to organization settings (select the organization in the project picker), e.g.{" "}
            <Link
              href="https://console.cloud.google.com/iam-admin/iam"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "#705d4a", fontWeight: 500 }}
            >
              Organization IAM
            </Link>
            .
          </Typography>
        </Box>
      </Typography>
    </Box>

    <Typography sx={STEP_SX}>Step 2: Grant Delegation</Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2, "& li": { mb: 0.75 } }}>
      <Typography component="li" sx={LI_SX}>
        Copy the <strong>Unique ID (Client ID)</strong> from the Service Account details.
      </Typography>
      <Typography component="li" sx={LI_SX}>
        Log into the{" "}
        <Link
          href="https://admin.google.com/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "#705d4a", fontWeight: 500 }}
        >
          Google Workspace Admin Console
        </Link>
        .
      </Typography>
      <Typography component="li" sx={LI_SX}>
        <strong>Create or grant access to the Super Admin account</strong> that Theo will use:
        <Box component="ol" sx={{ m: 0, mt: 0.5, pl: 2.5, "& li": { mb: 0.5 } }}>
          <Typography component="li" sx={LI_SX}>
            Sign in with an existing Super Admin account.
          </Typography>
          <Typography component="li" sx={LI_SX}>
            Go to{" "}
            <Link
              href="https://admin.google.com/ac/users"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "#705d4a", fontWeight: 500 }}
            >
              Directory &gt; Users
            </Link>
            , then select the user Theo will impersonate. Create the user first if it does not exist.
          </Typography>
          <Typography component="li" sx={LI_SX}>
            Open <strong>Admin roles and privileges</strong>.
          </Typography>
          <Typography component="li" sx={LI_SX}>
            Select <strong>Super Admin</strong>, set it to <strong>Assigned</strong>, and save.
          </Typography>
          <Typography component="li" sx={LI_SX}>
            Wait a few minutes for the role to propagate, then enter this user&apos;s email in the{" "}
            <strong>Super Admin Email</strong> field below.
          </Typography>
        </Box>
      </Typography>
      <Typography component="li" sx={LI_SX}>
        Navigate to{" "}
        <Link
          href={GWORKSPACE_DOMAIN_WIDE_DELEGATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "#705d4a", fontWeight: 500 }}
        >
          Security &gt; Access and data control &gt; API controls &gt; MANAGE DOMAIN WIDE DELEGATION
        </Link>
        .
      </Typography>
      <Typography component="li" sx={LI_SX}>
        Add a new client and paste the <strong>Client ID</strong> from Step 1.
      </Typography>
      <Typography component="li" sx={LI_SX}>
        <strong>OAuth Scopes:</strong> Paste the following comma-separated scopes:
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            bgcolor: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: 1,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: 11,
              color: "#333",
              lineHeight: 1.5,
              flex: 1,
              wordBreak: "break-all",
            }}
          >
            {gworkspaceOAuthScopesCsv}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
            <IconButton
              size="small"
              onClick={handleCopyScopes}
              sx={{ color: scopesCopied ? "#2e7d32" : "#705d4a", p: 0.25 }}
              title={scopesCopied ? "Copied" : "Copy scopes"}
              aria-label={scopesCopied ? "OAuth scopes copied" : "Copy OAuth scopes"}
            >
              {scopesCopied ? (
                <CheckIcon sx={{ fontSize: 16 }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
            {scopesCopied ? (
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#2e7d32",
                  lineHeight: 1.2,
                }}
              >
                Copied
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Typography>
      <Typography component="li" sx={LI_SX}>
        Click <strong>AUTHORIZE</strong>.
      </Typography>
    </Box>

    <Typography sx={STEP_SX}>Step 3: Enable APIs</Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2, "& li": { mb: 0.75 } }}>
      {GWORKSPACE_APIS.map((api) => (
        <Typography key={api.label} component="li" sx={LI_SX}>
          <Link
            href={api.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: "#705d4a", fontWeight: 500 }}
          >
            {api.label}
          </Link>
        </Typography>
      ))}
    </Box>

    <Box
      sx={{
        p: 1.5,
        bgcolor: "#fff",
        border: "1px solid #e5e5e5",
        borderRadius: 1,
        mb: 1,
      }}
    >
      <Typography sx={{ ...STEP_SX, mb: 0.75 }}>Action Required</Typography>
      <Typography sx={{ ...LI_SX, mb: 0.5 }}>
        Once setup is complete, provide the following to finalize configuration:
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
        <Typography component="li" sx={LI_SX}>
          <strong>JSON Key File</strong> — upload the service account JSON below.
        </Typography>
        <Typography component="li" sx={LI_SX}>
          <strong>Super Admin Email</strong> — enter the Super Admin email below (no password needed).
        </Typography>
        <Typography component="li" sx={LI_SX}>
          Click <strong>Test</strong> below to verify that the required Google APIs are enabled.
        </Typography>
      </Box>
    </Box>
  </>
  );
};
